import { z } from 'zod'
import { BacktestSimMode } from '../../enums/backtest-sim-mode.enum'
import { BacktestRunStatus } from '../../enums/backtest-run-status.enum'
import { backtestConfigSchema } from './backtest-config.schema'
import { backtestMetricsSchema } from './backtest-result.schema'

// integer-typed numeric settings: step + endpoints must be whole numbers
export const SWEEP_NUMERIC_INT_FIELDS = [
  'confirmationTicks',
  'confirmationSeconds',
  'lotsPerStep',
  'maxLotsPerBuy',
  'maxLotsPerSell',
  'maxHeldLots',
] as const
export const SWEEP_NUMERIC_FLOAT_FIELDS = [
  'dropPct',
  'shiftPct',
  'risePct',
  'sellShiftPct',
  'commissionRate',
  'spreadCushion',
  'dropPctMax',
  'shiftPctMax',
  'moodRate',
  'desireRate',
] as const
export const SWEEP_BOOLEAN_FIELDS = ['averageDown', 'closeLotsSameDay', 'sellFromAverage'] as const
export const SWEEP_ENUM_FIELDS = ['simMode'] as const
export const SWEEP_NUMERIC_FIELDS: readonly string[] = [
  ...SWEEP_NUMERIC_INT_FIELDS,
  ...SWEEP_NUMERIC_FLOAT_FIELDS,
]
export const MAX_SWEEP_AXES = 20 // axis-count cap
export const MAX_SWEEP_AXIS_STEPS = 100 // per-axis value cap
export const MAX_SWEEP_CELLS = 50000 // product (total runs) cap

// discriminated-union members must be bare ZodObjects; cross-field numeric
// validation is deferred to config-level superRefine in Task 2
export const sweepAxisSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('number'),
    field: z.enum([...SWEEP_NUMERIC_INT_FIELDS, ...SWEEP_NUMERIC_FLOAT_FIELDS] as [
      string,
      ...string[],
    ]),
    from: z.number(),
    to: z.number(),
    step: z.number().positive(),
  }),
  z.object({
    kind: z.literal('boolean'),
    field: z.enum(SWEEP_BOOLEAN_FIELDS as unknown as [string, ...string[]]),
  }),
  z.object({
    kind: z.literal('enum'),
    field: z.enum(SWEEP_ENUM_FIELDS as unknown as [string, ...string[]]),
    values: z.array(z.nativeEnum(BacktestSimMode)).min(1),
  }),
])

export type SweepAxis = z.infer<typeof sweepAxisSchema>

// expands an axis into its concrete list of values; float fields use toFixed(10)
// to guard against IEEE-754 drift; int fields are rounded to nearest integer
export function expandAxis(axis: SweepAxis): Array<number | boolean | string> {
  if (axis.kind === 'boolean') return [false, true]
  if (axis.kind === 'enum') return [...axis.values]
  const isInt = (SWEEP_NUMERIC_INT_FIELDS as readonly string[]).includes(axis.field)
  // a non-positive / non-finite step would make count Infinity and loop unbounded
  if (!(axis.step > 0) || !Number.isFinite(axis.step)) return []
  // assumes from <= to (enforced by sweepConfigSchema upstream); guard defensively
  const count = Math.floor((axis.to - axis.from) / axis.step + 1e-9) + 1
  if (count <= 0 || !Number.isFinite(count)) return []
  const out: number[] = []
  for (let i = 0; i < count; i++) {
    const v = axis.from + i * axis.step
    out.push(isInt ? Math.round(v) : Number(v.toFixed(10)))
  }
  return out
}

// returns the number of distinct values an axis produces; arithmetic only — never
// builds (nor infinitely loops over) an array, so it is safe to call on every render
export function sweepRunCount(axis: SweepAxis): number {
  if (axis.kind === 'boolean') return 2
  if (axis.kind === 'enum') return axis.values.length
  // a non-positive / non-finite step is invalid (and transient during editing);
  // report 0 runs rather than building (or infinitely looping over) an array
  if (!(axis.step > 0) || !Number.isFinite(axis.step)) return 0
  const count = Math.floor((axis.to - axis.from) / axis.step + 1e-9) + 1
  return count > 0 && Number.isFinite(count) ? count : 0
}

// computes the Cartesian product of each axis's expanded values, axes[0] outermost so the
// last axis varies fastest. each element is a value-tuple aligned to `axes`.
export function expandGrid(axes: SweepAxis[]): Array<Array<number | boolean | string>> {
  return axes.reduce<Array<Array<number | boolean | string>>>(
    (acc, axis) => {
      const vals = expandAxis(axis)
      const next: Array<Array<number | boolean | string>> = []
      for (const tuple of acc) for (const v of vals) next.push([...tuple, v])
      return next
    },
    [[]]
  )
}

// total number of grid cells (runs) = product of per-axis sizes
export function sweepGridCount(axes: SweepAxis[]): number {
  return axes.reduce((n, axis) => n * sweepRunCount(axis), 1)
}

export const sweepConfigSchema = z
  .object({
    baseConfig: backtestConfigSchema,
    axes: z.array(sweepAxisSchema).min(1).max(MAX_SWEEP_AXES),
  })
  .superRefine((d, ctx) => {
    if (d.baseConfig.instruments.length !== 1)
      ctx.addIssue({
        code: 'custom',
        path: ['baseConfig', 'instruments'],
        message: 'sweep requires exactly one instrument',
      })

    // every axis must sweep a distinct field — duplicates would write the same
    // override twice with an ambiguous grid ordering
    const fields = d.axes.map((a) => a.field)
    if (new Set(fields).size !== fields.length)
      ctx.addIssue({
        code: 'custom',
        path: ['axes'],
        message: 'each axis must sweep a distinct field',
      })

    d.axes.forEach((axis, i) => {
      if (axis.kind === 'number') {
        if (axis.from > axis.to)
          ctx.addIssue({ code: 'custom', path: ['axes', i, 'from'], message: 'from must be <= to' })
        const isInt = (SWEEP_NUMERIC_INT_FIELDS as readonly string[]).includes(axis.field)
        if (
          isInt &&
          !(Number.isInteger(axis.from) && Number.isInteger(axis.to) && Number.isInteger(axis.step))
        )
          ctx.addIssue({
            code: 'custom',
            path: ['axes', i, 'step'],
            message: 'integer field requires integer from/to/step',
          })
      }
      if (sweepRunCount(axis) > MAX_SWEEP_AXIS_STEPS)
        ctx.addIssue({
          code: 'custom',
          path: ['axes', i, 'step'],
          message: `axis exceeds ${MAX_SWEEP_AXIS_STEPS} steps`,
        })
    })

    if (sweepGridCount(d.axes) > MAX_SWEEP_CELLS)
      ctx.addIssue({
        code: 'custom',
        path: ['axes'],
        message: `grid exceeds ${MAX_SWEEP_CELLS} cells`,
      })
  })
export type SweepConfigDto = z.infer<typeof sweepConfigSchema>

// a step is one grid cell: an aligned value-tuple (one entry per axis) and its metrics. a cell whose
// worker threw is a per-cell EXCLUSION — metrics is null and `reason` carries the error — so one bad
// parameter combination leaves a gap in the heatmap instead of discarding the whole grid (#234).
export const sweepStepSchema = z.object({
  values: z.array(z.union([z.number(), z.boolean(), z.string()])).min(1),
  metrics: backtestMetricsSchema.nullable(),
  reason: z.string().nullable().optional(),
})
export type SweepStepDto = z.infer<typeof sweepStepSchema>

export const sweepStatusDtoSchema = z.object({
  sweepId: z.string(),
  status: z.nativeEnum(BacktestRunStatus),
  progress: z.object({ done: z.number().int(), total: z.number().int() }),
  createdAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  instrumentUid: z.string(),
  baseConfig: backtestConfigSchema,
  axes: z.array(sweepAxisSchema),
  // distinct trading days in the candle window (same for every step); null until done
  tradingDays: z.number().int().nullable(),
  steps: z.array(sweepStepSchema),
})
export type SweepStatusDto = z.infer<typeof sweepStatusDtoSchema>

export const sweepSummarySchema = z.object({
  sweepId: z.string(),
  createdAt: z.coerce.date(),
  finishedAt: z.coerce.date().nullable(),
  status: z.nativeEnum(BacktestRunStatus),
  axes: z.array(z.object({ field: z.string(), kind: z.enum(['number', 'boolean', 'enum']) })),
  instrumentUid: z.string(),
  stepCount: z.number().int(),
  from: z.coerce.date(),
  to: z.coerce.date(),
})
export type SweepSummaryDto = z.infer<typeof sweepSummarySchema>
